import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle, Inbox } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { ContentCard, PageHeader } from "../../../components/ui";
import { TablePagination } from "../../leave-management/components/TablePagination";
import { InventoryActionToast } from "../components/InventoryActionToast";
import {
  RevokeUnlockRequestDetailModal,
  formatActionType,
  formatStatus,
  statusBadgeClass,
} from "../components/RevokeUnlockRequestDetailModal";
import { inventoryManagementService } from "../../../services/inventoryManagementService";
import { cn } from "../../../lib/utils";
import {
  allocationStatusClass,
  allocationStatusLabel,
} from "../utils/allocationStatus";

const PAGE_SIZE = 10;

function isPendingStatus(status) {
  return String(status || "").toUpperCase() === "PENDING";
}

export function RevokeUnlockRequestsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [page, setPage] = useState(1);
  const [actionError, setActionError] = useState("");
  const [successToast, setSuccessToast] = useState("");
  const [rowActionId, setRowActionId] = useState(null);
  const [detailRequest, setDetailRequest] = useState(null);

  const loadRequests = useCallback(() => {
    setLoading(true);
    setActionError("");
    inventoryManagementService
      .getPendingRevokeUnlockRequests()
      .then((res) => {
        setRequests(Array.isArray(res) ? res : []);
        setPage(1);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load revoke/unlock requests:", err);
        setRequests([]);
        setActionError(
          err?.message || t("inventory.revokeUnlockLoadError")
        );
        setLoading(false);
      });
  }, [t]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const totalPages = Math.max(1, Math.ceil(requests.length / PAGE_SIZE));

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return requests.slice(start, start + PAGE_SIZE);
  }, [requests, page]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const formatQty = (val) => {
    const n = Number(val);
    return Number.isFinite(n) ? n.toLocaleString() : "—";
  };

  const runRowAction = (row, action, e) => {
    e?.stopPropagation?.();
    if (!row?.id || rowActionId) return;
    if (!isPendingStatus(row.status)) return;

    setRowActionId(`${row.id}:${action}`);
    setActionError("");

    const apiCall =
      action === "approve"
        ? inventoryManagementService.approveRevokeUnlockRequest(row.id)
        : inventoryManagementService.rejectRevokeUnlockRequest(row.id);

    apiCall
      .then(() => {
        const nextStatus = action === "approve" ? "APPROVED" : "REJECTED";
        setSuccessToast(
          action === "approve"
            ? t("inventory.revokeUnlockApprovedSuccess")
            : t("inventory.revokeUnlockRejectedSuccess")
        );
        setTimeout(() => setSuccessToast(""), 3000);
        setRequests((prev) =>
          prev.map((r) =>
            r.id === row.id ? { ...r, status: nextStatus } : r
          )
        );
        setDetailRequest((prev) =>
          prev?.id === row.id ? { ...prev, status: nextStatus } : prev
        );
        setRowActionId(null);
      })
      .catch((err) => {
        console.error(`Failed to ${action} revoke/unlock request:`, err);
        setActionError(
          err?.message ||
            (action === "approve"
              ? t("inventory.revokeUnlockApproveError")
              : t("inventory.revokeUnlockRejectError"))
        );
        setRowActionId(null);
      });
  };

  const renderActionButtons = (row) => {
    if (!isPendingStatus(row.status)) return null;
    const busy = rowActionId?.startsWith(`${row.id}:`);
    return (
      <div className="inline-flex items-center gap-2">
        <button
          type="button"
          disabled={Boolean(rowActionId)}
          onClick={(e) => runRowAction(row, "approve", e)}
          className="h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy && rowActionId === `${row.id}:approve`
            ? t("inventory.saving")
            : t("formActions.approve")}
        </button>
        <button
          type="button"
          disabled={Boolean(rowActionId)}
          onClick={(e) => runRowAction(row, "reject", e)}
          className="h-8 px-3 rounded-lg border border-rose-200 bg-white text-rose-700 hover:bg-rose-50 text-[11px] font-bold transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy && rowActionId === `${row.id}:reject`
            ? t("inventory.saving")
            : t("formActions.reject")}
        </button>
      </div>
    );
  };

  return (
    <AppLayout
      breadcrumb={[
        { label: t("inventory.operations") },
        {
          label: t("inventory.inventoryManagement"),
          onClick: () => navigate("/inventory-management/stock-position"),
        },
        { label: t("inventory.revokeUnlockRequests") },
      ]}
      className="space-y-6 animate-fade-in"
    >
      {successToast ? <InventoryActionToast toast={successToast} /> : null}

      <RevokeUnlockRequestDetailModal
        open={Boolean(detailRequest)}
        onOpenChange={(open) => {
          if (!open) setDetailRequest(null);
        }}
        request={detailRequest}
      />

      <PageHeader
        back={
          <PageBackLink
            to="/inventory-management/stock-position"
            label={t("formActions.back")}
          />
        }
        title={t("inventory.revokeUnlockRequests")}
        subtitle={t("inventory.revokeUnlockRequestsSubtitle")}
      />

      {actionError ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs font-bold text-rose-700">
          {actionError}
        </div>
      ) : null}

      <ContentCard className="overflow-hidden">
        <div className="px-4 sm:px-6 py-3 border-b border-(--color-text-border)/70 flex flex-wrap items-center justify-between gap-3 bg-white font-sans">
          <span className="text-xs font-bold text-foreground">
            {t("inventory.revokeUnlockRequests")}
          </span>
        </div>

        {loading ? (
          <div className="p-6 space-y-4 animate-pulse">
            <div className="h-10 bg-gray-100 rounded w-full" />
            <div className="space-y-2">
              {[1, 2, 3].map((idx) => (
                <div key={idx} className="h-12 bg-gray-50/50 rounded w-full" />
              ))}
            </div>
          </div>
        ) : requests.length === 0 ? (
          <div className="p-12 flex flex-col items-center text-center space-y-4 max-w-sm mx-auto font-sans">
            <div className="h-12 w-12 rounded-full bg-emerald-50 text-emerald-600 grid place-items-center shadow-xs">
              {actionError ? (
                <Inbox className="h-6 w-6 text-rose-500" />
              ) : (
                <CheckCircle className="h-6 w-6" />
              )}
            </div>
            <div>
              <h4 className="text-sm font-bold text-foreground">
                {t("inventory.noRevokeUnlockRequests")}
              </h4>
              <p className="text-xs per-file-hint mt-1.5 leading-relaxed">
                {t("inventory.noRevokeUnlockRequestsHint")}
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/inventory-management/stock-position")}
              className="inline-flex items-center justify-center h-10 px-5 rounded-xl bg-primary text-white font-bold text-xs hover:bg-accent transition cursor-pointer"
            >
              {t("inventory.returnToStockPosition")}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm min-w-[1180px] border-collapse font-sans">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider per-file-hint bg-workspace/30 border-b border-(--color-text-border)/65 font-bold">
                    <th className="px-4 py-3 text-left">
                      {t("inventory.reference")}
                    </th>
                    <th className="px-4 py-3 text-left">
                      {t("inventory.customerName")}
                    </th>
                    <th className="px-4 py-3 text-left">
                      {t("inventory.productName")}
                    </th>
                    <th className="px-4 py-3 text-left">
                      {t("inventory.actionType")}
                    </th>
                    <th className="px-4 py-3 text-left">
                      {t("inventory.status")}
                    </th>
                    <th className="px-4 py-3 text-left">
                      {t("inventory.lineStatus")}
                    </th>
                    <th className="px-4 py-3 text-right">
                      {t("inventory.revokeQuantity")}
                    </th>
                    <th className="px-4 py-3 text-left">
                      {t("inventory.reason")}
                    </th>
                    <th className="px-4 py-3 text-right">
                      {t("common.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 font-medium">
                  {paginatedRows.map((row) => {
                    const actionUpper = String(row.actionType || "").toUpperCase();
                    return (
                      <tr
                        key={row.id}
                        className="hover:bg-gray-50/50 transition-colors cursor-pointer"
                        onClick={() => setDetailRequest(row)}
                      >
                        <td className="px-4 py-3 font-mono text-xs font-semibold text-foreground truncate">
                          {row.reference || "—"}
                        </td>
                        <td className="px-4 py-3 text-foreground truncate">
                          {row.customerName || "—"}
                        </td>
                        <td className="px-4 py-3 text-foreground truncate">
                          {row.productName || "—"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={cn(
                              "inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-extrabold uppercase tracking-wider border",
                              actionUpper === "UNLOCK"
                                ? "bg-sky-50 text-sky-700 border-sky-200"
                                : "bg-rose-50 text-rose-700 border-rose-200"
                            )}
                          >
                            {formatActionType(row.actionType, t)}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={cn(
                              "inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-extrabold uppercase tracking-wider border",
                              statusBadgeClass(row.status)
                            )}
                          >
                            {formatStatus(row.status, t)}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {row.lineStatus && row.lineStatus !== "—" ? (
                            <span
                              className={cn(
                                "inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-extrabold tracking-wider border",
                                allocationStatusClass(row.lineStatus)
                              )}
                            >
                              {allocationStatusLabel(row.lineStatus, t)}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-mono font-semibold text-foreground whitespace-nowrap">
                          {actionUpper === "UNLOCK"
                            ? "—"
                            : formatQty(row.revokeQuantity)}
                        </td>
                        <td className="px-4 py-3 per-file-hint max-w-[220px] truncate">
                          {row.reason || "—"}
                        </td>
                        <td
                          className="px-4 py-3 text-right whitespace-nowrap"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {renderActionButtons(row) || (
                            <span className="text-[11px] per-file-hint font-semibold">
                              —
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <ul className="md:hidden space-y-2.5 p-3">
              {paginatedRows.map((row) => {
                const actionUpper = String(row.actionType || "").toUpperCase();
                return (
                  <li
                    key={row.id}
                    className="rounded-xl border border-(--color-text-border)/60 bg-white px-3.5 py-3 space-y-2.5 shadow-xs cursor-pointer"
                    onClick={() => setDetailRequest(row)}
                  >
                    <div className="min-w-0 flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground truncate">
                          {row.productName || "—"}
                        </p>
                        <p className="text-xs per-file-hint mt-0.5 truncate">
                          {row.customerName || "—"}
                        </p>
                        <p className="text-xs font-mono per-file-hint mt-0.5">
                          {row.reference || "—"}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <span
                          className={cn(
                            "inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-extrabold uppercase tracking-wider border",
                            actionUpper === "UNLOCK"
                              ? "bg-sky-50 text-sky-700 border-sky-200"
                              : "bg-rose-50 text-rose-700 border-rose-200"
                          )}
                        >
                          {formatActionType(row.actionType, t)}
                        </span>
                        <span
                          className={cn(
                            "inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-extrabold uppercase tracking-wider border",
                            statusBadgeClass(row.status)
                          )}
                        >
                          {formatStatus(row.status, t)}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-1.5 pt-1 border-t border-(--color-text-border)/25 text-xs">
                      <div className="flex justify-between gap-3 items-center">
                        <span className="per-file-hint">
                          {t("inventory.lineStatus")}
                        </span>
                        {row.lineStatus && row.lineStatus !== "—" ? (
                          <span
                            className={cn(
                              "inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-extrabold tracking-wider border",
                              allocationStatusClass(row.lineStatus)
                            )}
                          >
                            {allocationStatusLabel(row.lineStatus, t)}
                          </span>
                        ) : (
                          <span className="font-semibold">—</span>
                        )}
                      </div>
                      {actionUpper !== "UNLOCK" ? (
                        <div className="flex justify-between gap-3">
                          <span className="per-file-hint">
                            {t("inventory.revokeQuantity")}
                          </span>
                          <span className="font-semibold font-mono">
                            {formatQty(row.revokeQuantity)}
                          </span>
                        </div>
                      ) : null}
                      <div className="flex justify-between gap-3">
                        <span className="per-file-hint">
                          {t("inventory.reason")}
                        </span>
                        <span className="font-semibold text-right max-w-[60%] truncate">
                          {row.reason || "—"}
                        </span>
                      </div>
                    </div>
                    {isPendingStatus(row.status) ? (
                      <div
                        className="flex items-center gap-2 pt-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          disabled={Boolean(rowActionId)}
                          onClick={(e) => runRowAction(row, "approve", e)}
                          className="flex-1 h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold transition cursor-pointer disabled:opacity-50"
                        >
                          {rowActionId === `${row.id}:approve`
                            ? t("inventory.saving")
                            : t("formActions.approve")}
                        </button>
                        <button
                          type="button"
                          disabled={Boolean(rowActionId)}
                          onClick={(e) => runRowAction(row, "reject", e)}
                          className="flex-1 h-9 rounded-lg border border-rose-200 bg-white text-rose-700 hover:bg-rose-50 text-[11px] font-bold transition cursor-pointer disabled:opacity-50"
                        >
                          {rowActionId === `${row.id}:reject`
                            ? t("inventory.saving")
                            : t("formActions.reject")}
                        </button>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>

            <div className="px-4 pb-4">
              <TablePagination
                page={page}
                totalPages={totalPages}
                totalItems={requests.length}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
              />
            </div>
          </div>
        )}
      </ContentCard>
    </AppLayout>
  );
}
